// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A session that can be slowed down (M9-23, 11 §4).
//
// *"Slow motion — 0.1× to 1× rendering; the simulation rate is unchanged."*
//
// **The simulation rate really is unchanged, and that is the whole design.** A step is one
// millisecond of machine at 1000 Hz whatever this is set to; what changes is how much wall clock a
// frame is allowed to *spend*. At 0.2× a sixteen-millisecond frame buys three steps instead of
// sixteen, so the ball crosses the playfield five times more slowly and every one of those steps is
// exactly the step it would have been at full speed. Nothing about the physics, the contact solver
// or the rules can tell the difference — which is the point, because a slow motion that changed the
// timestep would show an author a machine that does not exist.
//
// **It is a subclass rather than a setting, and only the Builder ever constructs one.**
// `PlaySession.timeScale`'s own comment says why: *"a player with a speed control is a player with a
// way to make every skill shot trivial and every leaderboard score meaningless."* Making it a field
// on the options would put that control one line away from every scored game in the product. This
// way a scored session does not have the override at all.

import { PlaySession } from './PlaySession'
import type { PlayControls } from '../input/desktop'

/** 11 §4's range. A tenth is slow enough to watch a contact; anything slower is a still. */
export const SLOWEST = 0.1
export const FASTEST = 1

export class TestPlaySession extends PlaySession {
  /**
   * How fast time runs, 0.1 to 1.
   *
   * Public and mutable because it is a control an author turns while watching, and routing it
   * through a method would be a method whose only job is to assign a number.
   */
  timeScale_ = FASTEST

  protected override timeScale(_controls: PlayControls): number {
    // Clamped here rather than trusted from the caller: a zero would stop the machine in a way that
    // is not the freeze button and cannot be undone by it, and a negative would run the accumulator
    // backwards.
    return Math.min(FASTEST, Math.max(SLOWEST, this.timeScale_))
  }
}
